/**
 * Client billing contacts — wire types + fetch/mutation helpers behind
 * the Pre-billing "Billing details" card (invoice workflow pass,
 * founder feedback 2026-07-10). Mirrors src/lib/clientRates.ts (same
 * tab; view_invoice reads; set_bill_to writes, and refreshes with
 * manage_client_identity).
 */

import { writeFetch } from './writeFetch';
import { useQuery } from '@tanstack/react-query';
import { ApiError } from '@/lib/api';

export interface BillingContactWire {
  id: string;
  contact_name: string;
  relationship: string | null;
  email: string | null;
  address: string | null;
  // The mailing address's parts (0224); null on every part: the address
  // is text only, and `address` holds it. Read with ?? null, so a row
  // from a build before 0224 reads as text only.
  address_line1?: string | null;
  address_line2?: string | null;
  address_city?: string | null;
  address_state?: string | null;
  address_zip?: string | null;
  is_primary: boolean;
  // 0149 (G8): provenance of a projected bill-to. source_contact_id != null
  // -> "Filled from a care contact" (the card NEVER shows the contact's
  // name — it's encrypted / RLS-gated; the projected name already lives on
  // contact_name). source_stale -> the "the care contact changed —
  // refresh?" nudge. Both read from THIS plain row only, never the clinical
  // contact.
  source_contact_id: string | null;
  source_stale: boolean;
  updated_at: string;
}

export interface BillingContactPutBody {
  /** Present = edit that row; absent = create. */
  id?: string;
  contact_name: string;
  relationship: string | null;
  email: string | null;
  /** The mailing address as its five parts, or null for none. Left out
   *  of an edit, the stored address is kept (a bill-to held as text only
   *  whose fields were left empty). */
  address?: BillingAddressParts | null;
  is_primary: boolean;
}

/** A bill-to's mailing address as its five parts; a blank part is null. */
export interface BillingAddressParts {
  line1: string | null;
  line2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
}

/** A stored bill-to's parts, or null when its address is text only or
 *  there is none (a street with any part: the street is always set). */
export function billingAddressPartsOf(row: BillingContactWire): BillingAddressParts | null {
  if ((row.address_line1 ?? null) === null) return null;
  return {
    line1: row.address_line1 ?? null,
    line2: row.address_line2 ?? null,
    city: row.address_city ?? null,
    state: row.address_state ?? null,
    zip: row.address_zip ?? null,
  };
}

export function billingContactsKey(slug: string, clientId: string): readonly unknown[] {
  return ['client-billing-contacts', slug, clientId];
}

async function parseError(res: Response, label: string): Promise<ApiError> {
  let body: unknown;
  try {
    body = await res.json();
  } catch {
    body = await res.text().catch(() => null);
  }
  return new ApiError(res.status, body, `${label} → HTTP ${res.status}`);
}

function contactsPath(slug: string, clientId: string): string {
  return `/api/t/${encodeURIComponent(slug)}/v1/clients/${encodeURIComponent(clientId)}/billing-contacts`;
}

export async function fetchBillingContacts(
  slug: string,
  clientId: string,
  signal?: AbortSignal,
): Promise<BillingContactWire[]> {
  const path = contactsPath(slug, clientId);
  const res = await fetch(path, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    signal,
  });
  if (!res.ok) throw await parseError(res, `GET ${path}`);
  const payload = (await res.json()) as { contacts: BillingContactWire[] };
  return payload.contacts;
}

export async function putBillingContact(
  slug: string,
  clientId: string,
  body: BillingContactPutBody,
): Promise<BillingContactWire[]> {
  const path = contactsPath(slug, clientId);
  const res = await writeFetch(path, {
    method: 'PUT',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    redirect: 'manual',
  });
  if (!res.ok) throw await parseError(res, `PUT ${path}`);
  const payload = (await res.json()) as { contacts: BillingContactWire[] };
  return payload.contacts;
}

/**
 * 0149 (G8) — re-project a projected bill-to from its source care contact
 * (the clinical-read-gated Refresh). POSTs to .../{id}/refresh; the server
 * decrypts the source contact, re-projects through the billing single
 * writer, and clears source_stale. Returns the refreshed list. 403 when the
 * caller lacks clinical read (a billing-only seat); the card gates the
 * button on that capability, so this is the defense-in-depth path.
 */
export async function refreshBillingContact(
  slug: string,
  clientId: string,
  id: string,
): Promise<BillingContactWire[]> {
  const path = `${contactsPath(slug, clientId)}/${encodeURIComponent(id)}/refresh`;
  const res = await writeFetch(path, {
    method: 'POST',
    headers: { Accept: 'application/json' },
    redirect: 'manual',
  });
  if (!res.ok) throw await parseError(res, `POST ${path}`);
  const payload = (await res.json()) as { contacts: BillingContactWire[] };
  return payload.contacts;
}

export interface UseBillingContactsResult {
  contacts: BillingContactWire[] | null;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
}

export function useBillingContacts(slug: string, clientId: string): UseBillingContactsResult {
  const query = useQuery<BillingContactWire[]>({
    queryKey: billingContactsKey(slug, clientId),
    queryFn: ({ signal }) => fetchBillingContacts(slug, clientId, signal),
    staleTime: 30_000,
    enabled: slug !== '' && clientId !== '',
  });
  return {
    contacts: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: () => {
      void query.refetch();
    },
  };
}

/** The card's subject: the one primary bill-to, or null (attention state). */
export function primaryContactOf(
  contacts: readonly BillingContactWire[] | null,
): BillingContactWire | null {
  if (contacts === null) return null;
  return contacts.find((c) => c.is_primary) ?? null;
}

/** The one email shape check the client pages share (an @ with a dot
 *  after it), and its one sentence. Where it is advisory (a contact's
 *  email) the text still travels; the bill-to refuses on it, because
 *  that address receives invoices. */
export function contactEmailProblem(v: string): string | null {
  const s = v.trim();
  if (s === '') return null;
  if (!/^\S+@\S+\.\S+$/.test(s)) {
    return "That isn't a complete email address. Enter the full address, with the @.";
  }
  return null;
}
